// Line Entry — the split button on a lines toolbar ("One button, several
// ways", canvas board 10): the primary command on the face, the rest in its
// menu in the author's order with dividers, each entry with a one-line
// description and its shortcut. The same button on the spreadsheet grid, the
// table view's lines tab and the form view's lines section.

import { useEffect, useRef, useState } from 'react'
import { ChevronDown, ListPlus, Loader2 } from 'lucide-react'
import { Z_ROW_MENU } from '../../layers'
import type { ToolbarLayout } from './model'
import { T, btnPri, kbd } from './styles'

interface Props {
  layout: ToolbarLayout
  busy: string | null
  disabled?: boolean
  onRun: (command: string) => void
  /** The line under each entry's label. */
  describe?: (name: string) => string
  /** "From PO-2026-0143" over the menu, when the record names its document. */
  menuTitle?: string
  /** Compact surfaces show just the icon + caret. */
  compact?: boolean
  /** Designer preview: draw the menu open and inert. */
  previewOpen?: boolean
}

const item: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: '2px 12px', width: '100%',
  boxSizing: 'border-box', padding: '9px 14px', border: 0, background: 'transparent',
  fontSize: 14, color: T.text, textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
}

export default function LineEntryMenu({
  layout, busy, disabled, onRun, describe, menuTitle, compact, previewOpen,
}: Props) {
  const [openState, setOpen] = useState(false)
  const open = previewOpen || openState
  const wrap = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!openState) return
    const onDoc = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [openState])

  const hasMenu = layout.menu.length > 0
  const face = layout.primary
  const isBusy = !!busy
  const off = disabled || isBusy
  const faceLabel = face ? face.label : layout.buttonLabel
  const toggle = () => { if (!previewOpen) setOpen(o => !o) }

  // The face command heads its own menu too (with its shortcut), as on the
  // canvas; the author's dividers follow.
  const entries = [...(face && hasMenu ? [face] : []), ...layout.menu]

  return (
    <div ref={wrap} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        disabled={off}
        onClick={() => (face ? onRun(face.name) : toggle())}
        title={face ? face.label : layout.buttonLabel}
        aria-label={compact ? faceLabel : undefined}
        style={{
          ...btnPri, borderRadius: hasMenu && face ? '6px 0 0 6px' : 6,
          opacity: off ? 0.65 : 1, cursor: off ? 'not-allowed' : 'pointer',
        }}
      >
        {isBusy
          ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
          : <ListPlus size={14} />}
        {!compact && faceLabel}
        {!face && hasMenu && <ChevronDown size={13} />}
      </button>
      {face && hasMenu && (
        <button
          type="button" disabled={off} onClick={toggle}
          aria-haspopup="menu" aria-expanded={open} aria-label="More ways to add lines"
          style={{
            ...btnPri, borderRadius: '0 6px 6px 0', borderLeft: '1px solid rgba(255,255,255,.45)',
            padding: '0 9px', opacity: off ? 0.65 : 1, cursor: off ? 'not-allowed' : 'pointer',
          }}
        ><ChevronDown size={14} /></button>
      )}
      {open && (hasMenu || !face) && (
        <div role="menu" aria-label="Add lines" style={{
          position: previewOpen ? 'relative' : 'absolute', top: previewOpen ? 6 : 'calc(100% + 6px)',
          right: 0, width: 330, maxWidth: '86vw', background: T.bg, border: `1px solid ${T.border}`,
          borderRadius: 10, boxShadow: '0 14px 36px rgba(20,28,40,.2)', padding: '6px 0',
          // The designer's always-open preview sits in the page flow: no layer of
          // its own, or it floats over the designer's dialogs.
          zIndex: previewOpen ? undefined : Z_ROW_MENU,
        }}>
          {menuTitle && (
            <div style={{ padding: '6px 14px 4px', fontSize: 13, fontWeight: 600, letterSpacing: '.05em',
              textTransform: 'uppercase', color: T.muted }}>{menuTitle}</div>
          )}
          {entries.map((entry, i) => (entry === 'sep' ? (
            <div key={`sep${i}`} role="separator" style={{ height: 1, margin: '6px 0', background: '#edf0f4' }} />
          ) : (
            <button
              key={entry.name} type="button" role="menuitem"
              onClick={() => { setOpen(false); if (!previewOpen) onRun(entry.name) }}
              style={item}
              onMouseEnter={e => { e.currentTarget.style.background = '#f0f3f7' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
            >
              <b style={{ fontWeight: 600 }}>{entry.label}</b>
              {entry.shortcut ? <span style={kbd}>{entry.shortcut}</span> : <span />}
              {describe && (
                <small style={{ gridColumn: '1 / -1', color: T.muted, fontSize: 13 }}>{describe(entry.name)}</small>
              )}
            </button>
          )))}
        </div>
      )}
    </div>
  )
}
